import {
  ActionId,
  ApiClient,
  AsyncCache,
  CancellationToken,
  // CancellationToken,
  ComponentModal,
  CustomLogicBase,
  CustomLogicEvent,
  DialogInputModal,
  DialogModal,
  FieldChangedArgs,
  FormDismissResult,
  FormDismissResultAggregate,
  FormModal,
  Module,
  sendNotification,
  UrlContext,
  ViewModal,
} from '@msdk/common'
import { CustomLogicDefinition, EntityContext, Message } from '@msdk/definitions'

import { resolveItem } from '../../utils'

export class CustomLogicInvoker {
  private readonly logics: CustomLogicDefinition[]
  private readonly module: Module
  private readonly instances: CustomLogicBase[]
  private readonly apiClient: ApiClient
  private readonly transalte: (idml: string, description: string) => string
  private readonly asyncCache: AsyncCache
  private readonly urlContext: UrlContext
  private readonly navigate: (
    url: string,
    sitemap?: string,
    additionalCtx?: Record<string, string>,
    query?: URLSearchParams,
  ) => void
  private readonly registerSignalrTopic: (topic: string, callback: (data: any) => void) => CancellationToken
  private readonly refetchEntity: (id?: string) => Promise<void>
  private readonly rebindEntity: (entityContext: EntityContext, pendingChanges: boolean) => void

  constructor(
    logics: CustomLogicDefinition[],
    module: Module,
    apiClient: ApiClient,
    modals: {
      formModal: FormModal
      viewModal: ViewModal
      componentModal: ComponentModal
      dialogModal: DialogModal
      dialogInputModal: DialogInputModal
    },
    transalte: (idml: string, description: string) => string,
    asyncCache: AsyncCache,
    urlContext: UrlContext,
    navigate: (url: string, sitemap?: string, additionalCtx?: Record<string, string>, query?: URLSearchParams) => void,
    registerSignalrTopic: (topic: string, callback: (data: any) => void) => CancellationToken,
    refetchEntity: (id?: string) => Promise<void>,
    rebindEntity: (entityContext: EntityContext, pendingChanges: boolean) => void,
  ) {
    this.logics = logics
    this.module = module
    this.apiClient = apiClient
    this.transalte = transalte
    this.asyncCache = asyncCache
    this.urlContext = urlContext
    this.navigate = navigate
    this.registerSignalrTopic = registerSignalrTopic
    this.refetchEntity = refetchEntity
    this.rebindEntity = rebindEntity
    this.instances = []
    this.logics
      .filter((l) => l.clientOnly)
      .forEach((l) => {
        const ctor = resolveItem(this.module, l.fullName)
        if (ctor) {
          this.instances.push(
            new ctor({
              apiClient: this.apiClient,
              modals,
              translate: this.transalte,
              asyncCache: this.asyncCache,
              urlContext: this.urlContext,
              navigate: this.navigate,
              registerSignalrTopic: this.registerSignalrTopic,
              refetchEntity: this.refetchEntity,
              rebindEntity: this.rebindEntity,
            }),
          )
        } else {
          sendNotification({
            message: this.transalte(
              'CMPSR_SERVICE_CUSTOM_LOGIC_FORM_ERROR',
              'The form is missing some logic and it may not work properly.',
            ),
            status: 'error',
          })
          console.error(`[CustomLogicInvoker] - custom logic not found, ${l.fullName}`)
        }
      })
  }

  public postLoad(entity: EntityContext) {
    const promises: Promise<void>[] = []
    this.instances.forEach((i) => {
      promises.push(i.postLoad(entity, {}))
    })
    return Promise.all(promises)
  }

  public onFieldChanged(entity: EntityContext, args: FieldChangedArgs) {
    const promises: Promise<void>[] = []
    this.instances.forEach((i) => {
      promises.push(i.onFieldChanged(entity, args, {}))
    })
    return Promise.all(promises)
  }

  public validate(entity: EntityContext) {
    const promises: Promise<void>[] = []
    this.instances.forEach((i) => {
      promises.push(i.validate(entity, {}))
    })
    return Promise.all(promises)
  }

  public async preSave(entity: EntityContext, e: CustomLogicEvent) {
    const promises: Promise<boolean>[] = []
    this.instances.forEach((i) => {
      promises.push(i.preSave(entity, e))
    })
    const results = await Promise.all(promises)
    return !results.some((r) => !r)
  }

  public async save(entity: EntityContext, e: CustomLogicEvent) {
    const promises: Promise<boolean>[] = []
    this.instances.forEach((i) => {
      promises.push(i.save(entity, e))
    })
    const results = await Promise.all(promises)
    return !results.some((r) => !r)
  }

  public postSave(entity: EntityContext, e: CustomLogicEvent) {
    const promises: Promise<void>[] = []
    this.instances.forEach((i) => {
      promises.push(i.postSave(entity, e))
    })
    return Promise.all(promises)
  }

  public onSaveFailure(messages: Message[], e: CustomLogicEvent) {
    const promises: Promise<void>[] = []
    this.instances.forEach((i) => {
      promises.push(i.onSaveFailure(messages, e))
    })
    return Promise.all(promises)
  }

  public async onFormDismiss(entity: EntityContext, pendingChanges: boolean): Promise<FormDismissResult> {
    const promises: Promise<FormDismissResult>[] = []
    this.instances.forEach((i) => {
      promises.push(i.onFormDismiss(entity, pendingChanges))
    })
    const results = await Promise.all(promises)
    return FormDismissResultAggregate.aggregate(results)
  }

  public async onFormAction(entity: EntityContext, actionId: ActionId) {
    const promises: Promise<boolean>[] = []
    this.instances.forEach((i) => {
      promises.push(i.onFormAction(entity, actionId, {}))
    })
    const results = await Promise.all(promises)
    return !results.some((r) => !r)
  }

  public onDestroy() {
    this.instances.forEach((i) => {
      i.onDestroy()
    })
  }
}
